import type { AuthState } from '../model/auth.types'
import { redirect } from '@tanstack/react-router'
import { isAuthenticated } from '@/shared/api'
import { useAuthStore } from '../model'

/**
 * Auth guard for protected routes
 * Used in route loaders to check if user is authenticated
 * Redirects to login page if not authenticated
 */
export async function requireAuth(): Promise<AuthState> {
  try {
    const authState = useAuthStore.getState()

    if (authState.isLoading) {
      // Wait a bit for the auth state to settle
      await new Promise(resolve => setTimeout(resolve, 400))
    }

    if (!isAuthenticated()) {
      throw redirect({
        to: '/login',
        search: {
          error: 'unauthenticated',
        },
        replace: true,
        resetScroll: true,
      })
    }

    return authState
  } catch (error) {
    if (error instanceof Response) {
      throw error
    }

    throw redirect({
      to: '/login',
      search: {
        error: 'unauthenticated',
      },
      replace: true,
      resetScroll: true,
    })
  }
}

/**
 * Auth guard for public routes
 * Used in route loaders to check if user is already authenticated
 * Redirects to dashboard if authenticated
 */
export async function requireNoAuth(): Promise<void> {
  const authState = useAuthStore.getState()

  if (authState.isLoading) {
    // Wait a bit for the auth state to settle
    await new Promise(resolve => setTimeout(resolve, 400))
  }

  // If user is authenticated, redirect to dashboard
  if (isAuthenticated()) {
    throw redirect({
      to: '/dashboard',
      search: {
        error: 'authenticated',
      },
    })
  }
}
